import { z } from 'zod'

export enum OcrQuotaPolicy {
  Cumulative = 'CUMULATIVE',
  Unlimited = 'UNLIMITED',
}

export enum OcrQuotaSubjectType {
  Individual = 'INDIVIDUAL',
  Organization = 'ORGANIZATION',
}

export enum OcrQuotaPeriodKind {
  TrialCumulative = 'TRIAL_CUMULATIVE',
}

const OcrQuotaWaitingSummarySchema = z.object({
  known_pages: z.number().int().nonnegative(),
  known_files: z.number().int().nonnegative(),
})

const OcrQuotaReleasedUnconsumedSummarySchema = z.object({
  known_pages: z.number().int().nonnegative(),
  known_files: z.number().int().nonnegative(),
  oldest_released_at: z.string().datetime({ offset: true }).nullable(),
})

const OcrQuotaAdmittedUnconsumedSummarySchema = z.object({
  known_pages: z.number().int().nonnegative(),
  known_files: z.number().int().nonnegative(),
  oldest_admitted_at: z.string().datetime({ offset: true }).nullable(),
})

const OcrQuotaSummaryBaseSchema = z.object({
  is_trial: z.boolean().optional(),
  enforcement_enabled: z.boolean().optional(),
  subject_type: z.nativeEnum(OcrQuotaSubjectType),
  // Forward-compatible: nothing here branches on the period, so a new backend value must not fail every quota check.
  period_kind: z.string(),
  activation_at: z.string().datetime({ offset: true }).nullable(),
  effective_at: z.string().datetime({ offset: true }).nullable(),
  counted_pages: z.number().int().nonnegative(),
  own_contribution_pages: z.number().int().nonnegative(),
  consumed_file_count: z.number().int().nonnegative(),
  waiting: OcrQuotaWaitingSummarySchema,
  released_unconsumed: OcrQuotaReleasedUnconsumedSummarySchema,
  admitted_unconsumed: OcrQuotaAdmittedUnconsumedSummarySchema.nullish(),
})

const CumulativeOcrQuotaSummarySchema = OcrQuotaSummaryBaseSchema.extend({
  policy: z.literal(OcrQuotaPolicy.Cumulative),
  page_limit: z.number().int().positive(),
  remaining_pages: z.number().int().nonnegative(),
  is_exhausted: z.boolean(),
})

const UnlimitedOcrQuotaSummarySchema = OcrQuotaSummaryBaseSchema.extend({
  policy: z.literal(OcrQuotaPolicy.Unlimited),
  page_limit: z.null(),
  remaining_pages: z.null(),
  is_exhausted: z.literal(false),
})

export const OcrQuotaSummarySchema = z.discriminatedUnion('policy', [CumulativeOcrQuotaSummarySchema, UnlimitedOcrQuotaSummarySchema])

export type OcrQuotaSummary = z.infer<typeof OcrQuotaSummarySchema>
